import AddIcon from '@mui/icons-material/Add';
import { Button, Stack } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuthContext } from '../../../../../context/auth';
import { errorToast, successToast } from '../../../../Toast';
import { TenantsApi } from '../../../namespace/api';
import IntegrationsAPI, {
	ExternalServiceIntegration,
	ExternalServiceIntegrationAddRequest,
	ExternalServiceIntegrationUpdateRequest,
} from '../../api/integrations';
import {
	ADD_INTEGRATION_FAILURE,
	ADD_INTEGRATION_SUCCESS,
	DELETE_INTEGRATION_FAILURE,
	DELETE_INTEGRATION_SUCCESS,
	EDIT_INTEGRATION_FAILURE,
	EDIT_INTEGRATION_SUCCESS,
	EXTERNAL_RATER_LABEL,
	EXTERNAL_RATER_TYPE,
	NEW_LABEL,
} from '../../data/constants';
import IntegrationCard from './IntegrationCard';
import IntegrationSummary from './IntegrationSummary';
import AddUpdateIntegrationModal from './modals/AddUpdateIntegrationModal';
import ConfirmDeleteIntegrationModal from './modals/ConfirmDeleteIntegrationModal';

export default function ExternalRatersPanel() {
	const { tenantHostname } = useParams();
	const client = useQueryClient();
	// external integration types
	const authData = useAuthContext();
	const [showIntegrationModal, setShowIntegrationModal] = useState(false);
	const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
	const [selectedIntegration, setSelectedIntegration] =
		useState<ExternalServiceIntegration>(null);
	const authToken = authData?.tenantAuthorizationData?.authorizationToken;

	const queryKey = [
		'externalRaters',
		authData?.tenantAuthorizationData?.tenantId || tenantHostname,
	];

	const { data: externalRaters } = IntegrationsAPI.useExternalRaterIntegrations(
		queryKey,
		authToken,
		tenantHostname,
	);
	const { mutate: addExternalRater, isLoading: isAddingExternalRater } =
		useMutation(
			(newExternalRater: ExternalServiceIntegrationAddRequest) => {
				return tenantHostname
					? TenantsApi.addIntegration(newExternalRater, tenantHostname)
					: IntegrationsAPI.addIntegration(newExternalRater, authToken);
			},
			{
				onSuccess: () => {
					client.invalidateQueries(queryKey);
					successToast(ADD_INTEGRATION_SUCCESS());
					setShowIntegrationModal(false);
				},
				onError: (err) => {
					if (axios.isAxiosError(err) && err.response.data.message) {
						errorToast(
							ADD_INTEGRATION_FAILURE() + ': ' + err.response.data.message,
						);
					} else {
						errorToast(ADD_INTEGRATION_FAILURE());
					}
				},
			},
		);

	const { mutate: updateExternalRater, isLoading: isUpdatingExternalRater } =
		useMutation(
			(updatedExternalRater: ExternalServiceIntegrationUpdateRequest) => {
				return tenantHostname
					? TenantsApi.editIntegration(
							updatedExternalRater,
							selectedIntegration.name,
							tenantHostname,
					  )
					: IntegrationsAPI.editIntegration(
							updatedExternalRater,
							selectedIntegration.name,
							authToken,
					  );
			},
			{
				onSuccess: () => {
					client.invalidateQueries(queryKey);
					successToast(EDIT_INTEGRATION_SUCCESS());
					setShowIntegrationModal(false);
				},
				onError: (err) => {
					if (axios.isAxiosError(err) && err.response.data.message) {
						errorToast(
							EDIT_INTEGRATION_FAILURE() + ': ' + err.response.data.message,
						);
					} else {
						errorToast(EDIT_INTEGRATION_FAILURE());
					}
				},
			},
		);

	const { mutate: removeExternalRater, isLoading: isRemovingExternalRater } =
		useMutation(
			() => {
				return tenantHostname
					? TenantsApi.deleteIntegration(
							selectedIntegration.name,
							tenantHostname,
					  )
					: IntegrationsAPI.deleteIntegration(
							selectedIntegration.name,
							authToken,
					  );
			},
			{
				onSuccess: () => {
					client.invalidateQueries(queryKey);
					successToast(DELETE_INTEGRATION_SUCCESS());
					setShowConfirmDeleteModal(false);
				},
				onError: (err) => {
					if (axios.isAxiosError(err) && err.response.data.message) {
						errorToast(
							DELETE_INTEGRATION_FAILURE() + ': ' + err.response.data.message,
						);
					} else {
						errorToast(DELETE_INTEGRATION_FAILURE());
					}
				},
			},
		);

	const isLoading =
		isAddingExternalRater || isUpdatingExternalRater || isRemovingExternalRater;

	const handleAddIntegration = async (
		integration: ExternalServiceIntegration,
	) => {
		addExternalRater(integration);
	};

	const handleEditIntegration = (integration: ExternalServiceIntegration) => {
		updateExternalRater(integration);
	};

	const handleDeleteIntegration = (integration: ExternalServiceIntegration) => {
		removeExternalRater();
	};

	const handleOpenAddIntegrationModal = () => {
		setSelectedIntegration(null);
		setShowIntegrationModal(true);
	};

	const handleOpenEditIntegrationModal = (
		integration: ExternalServiceIntegration,
	) => {
		setSelectedIntegration(integration);
		setShowIntegrationModal(true);
	};

	const handleOpenDeleteIntegrationModal = (
		integration: ExternalServiceIntegration,
	) => {
		setSelectedIntegration(integration);
		setShowConfirmDeleteModal(true);
	};

	const onCancel = () => {
		setSelectedIntegration(null);
		setShowIntegrationModal(false);
		setShowConfirmDeleteModal(false);
	};

	return (
		<Stack
			direction='column'
			spacing={2}
			width={540}
			flex={1}
			sx={{ overflowY: 'auto' }}
		>
			<Button
				variant='contained'
				color='primary'
				sx={{ width: 'fit-content' }}
				startIcon={<AddIcon />}
				onClick={handleOpenAddIntegrationModal}
			>
				{NEW_LABEL(EXTERNAL_RATER_LABEL)}
			</Button>
			<IntegrationSummary
				title={EXTERNAL_RATER_LABEL(true)}
				count={externalRaters?.length}
			/>
			{showIntegrationModal ? (
				<AddUpdateIntegrationModal
					integrationNames={
						externalRaters ? externalRaters.map((rater) => rater.name) : []
					}
					open={showIntegrationModal}
					integrationType={EXTERNAL_RATER_TYPE}
					integrationTypeLabel={EXTERNAL_RATER_LABEL}
					integration={selectedIntegration}
					onCancel={onCancel}
					onAdd={handleAddIntegration}
					onEdit={handleEditIntegration}
					isLoading={isLoading}
				/>
			) : null}
			{showConfirmDeleteModal ? (
				<ConfirmDeleteIntegrationModal
					open={showConfirmDeleteModal}
					integrationTypeLabel={EXTERNAL_RATER_LABEL}
					integration={selectedIntegration}
					onCancel={onCancel}
					onDelete={handleDeleteIntegration}
				/>
			) : null}
			{externalRaters?.map((integration, i) => (
				<IntegrationCard
					integration={integration}
					key={EXTERNAL_RATER_TYPE() + i}
					onEdit={handleOpenEditIntegrationModal}
					onDelete={handleOpenDeleteIntegrationModal}
				/>
			))}
		</Stack>
	);
}
